import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { useParams, useNavigate } from "react-router";
import { useLibraryNode, useUpdatePage, useTableNodes } from "../../lib/hooks/use-kuvio";
import { Card, EmptyState, EditableTitle, Breadcrumb, Pill } from "../components/primitives";
import type { BreadcrumbSegment } from "../components/primitives";
import { Markdown } from "../components/Markdown";
import { pageSurfaceMeta, pageStatusMeta, PAGE_SURFACES, PAGE_STATUSES } from "../lib/page-meta";
import { VerificationBadge } from "../components/verification/VerificationBadge";
// №16 D-WF13 — legacy page-workflow frontmatter chrome retired.

type SaveState = "idle" | "saving" | "saved";

/**
 * Full-page view + editor for a page node (Sprint 5o S5O.24–28). A minimal but
 * functional markdown editor: a monospace textarea on the left, live preview on
 * the right, autosave-on-blur. Surface/status are inline dropdowns; the title
 * uses the shared <EditableTitle> primitive.
 *
 * Rendered by LibraryMemoryPage when the node at /library/:id is a page, so it
 * shares the cached get_node fetch (no extra round-trip).
 */
export function PageView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const node = useLibraryNode(id);
  const update = useUpdatePage();

  const [draft, setDraft] = useState("");
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const savedRef = useRef("");
  const loadedIdRef = useRef<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // (Re)initialise the editor when the loaded page changes. Polling refetches of
  // the same page don't clobber an in-progress edit — only an id change resets.
  useEffect(() => {
    if (node.data && loadedIdRef.current !== node.data.id) {
      loadedIdRef.current = node.data.id;
      setDraft(node.data.document ?? "");
      savedRef.current = node.data.document ?? "";
      setSaveState("idle");
    }
  }, [node.data]);

  if (node.isLoading) {
    return (
      <div style={{ maxWidth: 980 }}>
        <div className="kuvio-skeleton" style={{ height: 160, borderRadius: "var(--border-radius-lg)", background: "var(--color-background-secondary)" }} />
      </div>
    );
  }

  if (node.error || !node.data) {
    return (
      <Card>
        <EmptyState>{node.error?.message ?? "Page not found"}</EmptyState>
      </Card>
    );
  }

  const page = node.data;
  const pageId = page.id;
  const surface = pageSurfaceMeta(page.surface);

  async function saveBody() {
    if (draft === savedRef.current) return;
    const next = draft;
    setSaveState("saving");
    try {
      await update.mutateAsync({ id: pageId, body: next });
      savedRef.current = next;
      setSaveState("saved");
    } catch {
      setSaveState("idle");
    }
  }

  function changeSurface(value: string) {
    update.mutate({ id: pageId, surface: value });
  }
  function changeStatus(value: string) {
    update.mutate({ id: pageId, status: value });
  }

  // Markdown shortcuts: Cmd/Ctrl+B bold, +I italic, +K link.
  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (!(e.metaKey || e.ctrlKey)) return;
    const key = e.key.toLowerCase();
    const wraps: Record<string, [string, string]> = {
      b: ["**", "**"],
      i: ["_", "_"],
      k: ["[", "](url)"],
    };
    const wrap = wraps[key];
    if (!wrap) return;
    e.preventDefault();
    const el = e.currentTarget;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = draft.slice(start, end);
    const next = draft.slice(0, start) + wrap[0] + selected + wrap[1] + draft.slice(end);
    setDraft(next);
    // Restore a sensible selection/caret after React re-renders.
    requestAnimationFrame(() => {
      const node2 = textareaRef.current;
      if (!node2) return;
      node2.focus();
      if (selected) {
        node2.selectionStart = start + wrap[0].length;
        node2.selectionEnd = end + wrap[0].length;
      } else {
        const caret = start + wrap[0].length;
        node2.selectionStart = caret;
        node2.selectionEnd = caret;
      }
    });
  }

  const segments: BreadcrumbSegment[] = (page._breadcrumb ?? [])
    .filter((b) => b.id !== page.id)
    .map((b) => ({
      label: b.name,
      onClick: () => (b.type === "agent" ? navigate(`/agents/${b.id}`) : navigate(`/library/${b.id}`)),
    }));
  segments.push({ label: page.name });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 980 }}>
      <Breadcrumb segments={segments} />

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <EditableTitle
          value={page.name}
          onSave={async (name) => { await update.mutateAsync({ id: pageId, name }); }}
        />
        <span style={{ ...surfaceTagStyle, background: surface.dotVar }} title={`Surface: ${surface.label}`} />
        <span style={saveHintStyle}>
          {saveState === "saving" ? "Saving…" : saveState === "saved" ? "Saved" : ""}
        </span>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <label style={pickerWrapStyle}>
          <span style={pickerLabelStyle}>Surface</span>
          <select value={page.surface ?? ""} onChange={(e) => changeSurface(e.target.value)} style={selectStyle}>
            {!page.surface && <option value="">—</option>}
            {PAGE_SURFACES.map((s) => (
              <option key={s} value={s}>{pageSurfaceMeta(s).label}</option>
            ))}
          </select>
        </label>
        <label style={pickerWrapStyle}>
          <span style={pickerLabelStyle}>Status</span>
          <select value={page.status ?? ""} onChange={(e) => changeStatus(e.target.value)} style={selectStyle}>
            {!page.status && <option value="">—</option>}
            {PAGE_STATUSES.map((s) => (
              <option key={s} value={s}>{pageStatusMeta(s).label}</option>
            ))}
          </select>
        </label>
      </div>

      <VerificationBadge frontmatter={page._frontmatter ?? page.frontmatter} />
      <PageEntityChips frontmatter={page._frontmatter ?? page.frontmatter} />


      <div style={splitStyle}>
        <div style={paneStyle}>
          <div style={paneLabelStyle}>Markdown</div>
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setSaveState("idle"); }}
            onBlur={() => { void saveBody(); }}
            onKeyDown={onKeyDown}
            placeholder="Write in markdown… ⌘B bold · ⌘I italic · ⌘K link"
            spellCheck
            style={editorStyle}
          />
        </div>
        <div style={paneStyle}>
          <div style={paneLabelStyle}>Preview</div>
          <Card style={previewCardStyle}>
            {draft.trim() ? <Markdown>{draft}</Markdown> : <EmptyState>Nothing to preview yet.</EmptyState>}
          </Card>
        </div>
      </div>
    </div>
  );
}

function extractPageEntities(fm: Record<string, unknown> | null | undefined): string[] {
  const raw = fm?.entities;
  if (Array.isArray(raw)) return raw.filter((x): x is string => typeof x === "string");
  if (typeof raw === "string" && raw.length > 0) return [raw];
  return [];
}

/**
 * Sprint 5u (S5U.23) — renders a page's declared `entities:` frontmatter as
 * chips that navigate to the referenced entity's table. Refs are `type/slug`
 * (e.g. "product/mdgraph-cloud") or a bare token; the type half resolves to a
 * table node. Pages without the declaration render nothing.
 */
function PageEntityChips({ frontmatter }: { frontmatter: Record<string, unknown> | null | undefined }) {
  const navigate = useNavigate();
  const tablesQuery = useTableNodes();
  const refs = extractPageEntities(frontmatter);
  if (refs.length === 0) return null;

  const tables = tablesQuery.data ?? [];
  const tableForType = (typeSlug: string | null) =>
    typeSlug ? tables.find((t) => t.entity_type_slug === typeSlug) ?? null : null;

  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
      <span style={{ fontSize: 12, color: "var(--color-text-tertiary)", fontWeight: 500 }}>References</span>
      {refs.map((ref) => {
        const slash = ref.indexOf("/");
        const typeSlug = slash > 0 ? ref.slice(0, slash) : null;
        const label = slash > 0 ? ref.slice(slash + 1) : ref;
        const table = tableForType(typeSlug);
        return (
          <Pill
            key={ref}
            variant={table ? "blue" : "gray"}
            icon="cube"
            onClick={table ? () => navigate(`/agents/${table.parent_id}/tables/${table.id}`) : undefined}
          >
            {typeSlug ? `${typeSlug}: ${label}` : label}
          </Pill>
        );
      })}
    </div>
  );
}

const surfaceTagStyle: CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: "50%",
  flexShrink: 0,
};

const saveHintStyle: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-tertiary)",
  marginLeft: "auto",
  fontVariantNumeric: "tabular-nums",
};

const pickerWrapStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
};

const pickerLabelStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
};

const selectStyle: CSSProperties = {
  padding: "5px 8px",
  fontSize: 12,
  color: "var(--color-text-primary)",
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-md)",
  fontFamily: "var(--font-sans)",
  cursor: "pointer",
};

const splitStyle: CSSProperties = {
  display: "flex",
  gap: 12,
  flexWrap: "wrap",
  alignItems: "stretch",
};

const paneStyle: CSSProperties = {
  flex: "1 1 360px",
  minWidth: 280,
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const paneLabelStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const editorStyle: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  minHeight: 420,
  padding: "12px 14px",
  fontSize: 13,
  lineHeight: 1.6,
  color: "var(--color-text-primary)",
  background: "var(--color-background-primary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-lg)",
  outline: "none",
  resize: "vertical",
  fontFamily: "var(--font-mono, ui-monospace, monospace)",
};

const previewCardStyle: CSSProperties = {
  minHeight: 420,
  overflowY: "auto",
};
